'use client'

import React, { useEffect, useRef, useState, useLayoutEffect, useMemo } from 'react'
import type { NewsTickerBlock as BlockProps } from '@/payload-types'
import RichText from '@/components/RichText'

interface TickerMessage {
  id: string
  messageIndex: number
  x: number
  width: number
}

export const NewsTickerBlock: React.FC<BlockProps> = ({ messages: messagesProp }) => {
  const messageData = messagesProp.filter(m => m.message)
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [messages, setMessages] = useState<TickerMessage[]>([])
  const currentMessageIndex = useRef(0)
  const uniqueId = useRef(0)
  const [speed, setSpeed] = useState(1)
  const gap = 64

  // measure width of next message using RichText
  const [nextMessageWidth, setNextMessageWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (measureRef.current) {
      setNextMessageWidth(measureRef.current.offsetWidth)
    }
  }, [messageData, currentMessageIndex])

  useEffect(() => {
    const container = containerRef.current
    if (!container || messageData.length === 0 || nextMessageWidth == null) return

    let animationFrame: number

    const animate = () => {
      setMessages(current => {
        const next = current
          .map(msg => ({
            ...msg,
            x: msg.x - speed
          }))
          .filter(msg => msg.x + msg.width > 0)

        // add new message if there is space
        if (
          next.length === 0 ||
          (next[next.length - 1].x + next[next.length - 1].width + gap < container.offsetWidth)
        ) {
          const lastX = next.length > 0
            ? next[next.length - 1].x + next[next.length - 1].width + gap
            : container.offsetWidth

          next.push({
            id: `msg-${uniqueId.current++}`,
            messageIndex: currentMessageIndex.current,
            x: lastX,
            width: nextMessageWidth
          })

          // move to next
          currentMessageIndex.current = (currentMessageIndex.current + 1) % messageData.length
        }

        return next
      })

      animationFrame = requestAnimationFrame(animate)
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [messageData, nextMessageWidth, speed])

  if (messageData.length === 0) return null

  return (
    <div ref={containerRef} className="w-full h-8 bg-black text-white overflow-hidden relative"
         onMouseEnter={() => setSpeed(0)}
         onMouseLeave={() => setSpeed(1)}>
      {messages.map(msg => (
        <div
          key={msg.id}
          className="absolute top-1 whitespace-nowrap"
          style={{ left: `${msg.x}px` }}
        >
          <RichText
            className={'primary-links'}
            content={messageData[msg.messageIndex].message!}
            enableProse={false}
            enableGutter={false}
          />
        </div>
      ))}
      {/* hidden measurement element */}
      <div
        ref={measureRef}
        className="absolute whitespace-nowrap opacity-0 pointer-events-none"
        style={{ visibility: 'hidden' }}
      >
        {messageData.length > 0 && (
          <RichText
            className={'primary-links'}
            content={messageData[currentMessageIndex.current].message!}
            enableProse={false}
            enableGutter={false}
          />
        )}
      </div>
    </div>
  )
}
